import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Operator } from '../../../core/multi_location_analytics_sdk/_internal/enums';
import {
  Aggregate,
  Dimension,
  GroupBy,
  QueryMetricsRequest,
  QueryMetricsResponse,
} from '../../../core/multi_location_analytics_sdk/_internal/objects';
import { MultiLocationAnalyticsService } from '../../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { DEVICES } from '../../../core/pagespeed.interface';
import { Site, SiteService } from '../../../core/site';
import { StatsApiService } from '../../../core/stats';
import { VitalMetricResult } from '../../../core/stats/stats.api.service';
import { VitalMetricRecord, VitalMetricsTable } from './vital-metrics.interface';

export class VitalMetricInfo {
  desktopValue: any;
  mobileValue: any;
}

export enum DISPLAY_TYPE {
  NO_UNITS,
  TIME_UNITS,
}

export enum METRICS_TYPE {
  LARGEST_CONTENTFUL_PAINT = 'largest-contentful-paint',
  TOTAL_BLOCKING_TIME = 'total-blocking-time',
  CUMULATIVE_LAYOUT_SHIFT = 'cumulative-layout-shift',
  FIRST_INPUT_DELAY = 'first-input-delay',
  FIRST_CONTENTFUL_PAINT = 'first-contentful-paint',
  INTERACTIVE = 'interactive',
  SPEED_INDEX = 'speed-index',
}

@Injectable()
export class VitalMetricsService {
  vitalMetrics$$: BehaviorSubject<VitalMetricsTable> = new BehaviorSubject<VitalMetricsTable>(undefined);
  vitalMetrics$: Observable<VitalMetricsTable> = this.vitalMetrics$$.asObservable();
  site$: Observable<Site>;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();
  error$$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
  error$: Observable<any> = this.error$$.asObservable();

  refreshing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  refreshing$: Observable<boolean> = this.refreshing$$.asObservable();

  tableData$: Observable<VitalMetricRecord[]> = of([]);

  constructor(
    private multiLocationAnalyticsService: MultiLocationAnalyticsService,
    private siteService: SiteService,
    private statsApiService: StatsApiService,
    private translateService: TranslateService,
    private alertService: SnackbarService,
  ) {
    this.site$ = this.siteService.site$;

    this.site$
      .pipe(
        tap(() => {
          this.loading$$.next(true);
        }),
        switchMap((site: Site) => {
          return this.multiLocationAnalyticsService.queryMetrics(
            GetVitalMetricsRequest(site.partnerId, site.accountId),
          );
        }),
        catchError((err) => {
          this.error$$.next(err);
          return of(null);
        }),
      )
      .subscribe((response: QueryMetricsResponse) => {
        this.vitalMetrics$$.next(this.parseQueryMetricResponse(response));
        this.loading$$.next(false);
      });
  }

  parseQueryMetricResponse(response: QueryMetricsResponse): VitalMetricsTable {
    const desktopCachedResults = this.getDesktopCachedResults();
    const mobileCachedResults = this.getMobileCachedResults();
    const metricMap = new Map<string, VitalMetricInfo>();
    let updateDate: Date = new Date();
    if (!!response && !!response.metricResults && response.metricResults.length > 0) {
      const metrics = response.metricResults[0].metrics.metrics;
      if (!!metrics && metrics.length > 0) {
        if (
          desktopCachedResults.updateDate > metrics[0].dimension &&
          mobileCachedResults.updateDate > metrics[0].dimension
        ) {
          this.fillVitalMetricResults(DEVICES.DESKTOP, metricMap, desktopCachedResults.vmResult);
          this.fillVitalMetricResults(DEVICES.MOBILE, metricMap, mobileCachedResults.vmResult);
          updateDate = new Date(desktopCachedResults.updateDate);
        } else {
          metrics.forEach((m) => {
            if (m.results.metrics.length > 0) {
              const result = m.results.metrics[0];
              if (!metricMap[result.dimension]) {
                metricMap[result.dimension] = new VitalMetricInfo();
              }
              const metric = metricMap[result.dimension];
              if (result.results.metrics.length > 0) {
                const metricResult = result.results.metrics[0];
                switch (metricResult.dimension) {
                  case DEVICES.DESKTOP:
                    metric.desktopValue = metricResult.measures[0];
                    break;
                  case DEVICES.MOBILE:
                    metric.mobileValue = metricResult.measures[0];
                    break;
                }
              }
            }
          });
          updateDate = new Date(metrics[0].dimension);
        }
      }
    }
    return {
      records: this.convertMetricMapToTable(metricMap),
      updateDate: updateDate,
    } as VitalMetricsTable;
  }

  fillVitalMetricResults(device: DEVICES, metricMap: Map<string, VitalMetricInfo>, vmResult: VitalMetricResult): void {
    if (vmResult) {
      Object.keys(vmResult).forEach((key) => {
        if (!metricMap[key]) {
          metricMap[key] = new VitalMetricInfo();
        }
        switch (device) {
          case DEVICES.DESKTOP:
            metricMap[key].desktopValue = vmResult[key];
            break;
          case DEVICES.MOBILE:
            metricMap[key].mobileValue = vmResult[key];
            break;
        }
      });
    }
  }

  convertMetricMapToTable(metricMap: Map<string, VitalMetricInfo>): VitalMetricRecord[] {
    const tableData: VitalMetricRecord[] = [];
    // The key order does matter
    if (metricMap[METRICS_TYPE.LARGEST_CONTENTFUL_PAINT]) {
      tableData.push({
        metricTitle: 'Largest Contentful Paint (LCP)',
        link: 'https://web.dev/optimize-lcp/',
        desktopDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.TIME_UNITS,
          1,
          metricMap[METRICS_TYPE.LARGEST_CONTENTFUL_PAINT].desktopValue,
        ),
        mobileDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.TIME_UNITS,
          1,
          metricMap[METRICS_TYPE.LARGEST_CONTENTFUL_PAINT].mobileValue,
        ),
      } as VitalMetricRecord);
    }

    // In the case of FID: if FID is not available use Total Blocking Time (TBT) instead
    // Currently FID isn't collected, so leave TBT here
    if (metricMap[METRICS_TYPE.TOTAL_BLOCKING_TIME]) {
      tableData.push({
        metricTitle: 'Total Blocking Time (TBT)',
        link: 'https://web.dev/tbt/',
        desktopDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.TIME_UNITS,
          1,
          metricMap[METRICS_TYPE.TOTAL_BLOCKING_TIME].desktopValue,
        ),
        mobileDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.TIME_UNITS,
          1,
          metricMap[METRICS_TYPE.TOTAL_BLOCKING_TIME].mobileValue,
        ),
      } as VitalMetricRecord);
    }

    if (metricMap[METRICS_TYPE.CUMULATIVE_LAYOUT_SHIFT]) {
      tableData.push({
        metricTitle: 'Cumulative Layout Shift (CLS)',
        link: 'https://web.dev/optimize-cls/',
        desktopDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.NO_UNITS,
          3,
          metricMap[METRICS_TYPE.CUMULATIVE_LAYOUT_SHIFT].desktopValue,
        ),
        mobileDisplayValue: this.getMetricDisplayValue(
          DISPLAY_TYPE.NO_UNITS,
          3,
          metricMap[METRICS_TYPE.CUMULATIVE_LAYOUT_SHIFT].mobileValue,
        ),
      } as VitalMetricRecord);
    }

    return tableData;
  }

  getMetricDisplayValue(type: DISPLAY_TYPE, precision: number, value: any): string {
    if (this.isNumber(value)) {
      const val = Number(value);
      let result = '';
      let unit = '';
      switch (type) {
        case DISPLAY_TYPE.NO_UNITS:
          result = val.toFixed(precision);
          break;
        case DISPLAY_TYPE.TIME_UNITS: {
          const res = Number((val / 1000).toFixed(precision));
          // if the value is 960, it will show as 1s
          if (res >= 1.0) {
            unit = ' s';
            result = (val / 1000).toFixed(precision);
          } else {
            unit = ' ms';
            result = val.toFixed(precision);
          }
          break;
        }
      }
      // removing trailing zeros
      return this.trimTrailingZeros(result) + unit;
    }
    return '';
  }

  isNumber(value: any): boolean {
    return value != null && value !== '' && !isNaN(Number(value.toString()));
  }

  trimTrailingZeros(value: string): string {
    return value.replace(/\.?0+$/, '');
  }

  getDesktopCachedResults(): any {
    return this.statsApiService.getDesktopCachedResults();
  }

  getMobileCachedResults(): any {
    return this.statsApiService.getMobileCachedResults();
  }

  instantFetch(domain: string): void {
    const siteUrl = 'https://' + domain;

    this.refreshing$$.next(true);
    combineLatest([
      this.statsApiService.deviceFetch(siteUrl, DEVICES.DESKTOP),
      this.statsApiService.deviceFetch(siteUrl, DEVICES.MOBILE),
    ]).subscribe(
      ([desktopScore, mobileScore]) => {
        // only update scores to table when both requests are successful
        if (!!desktopScore && !!desktopScore.vmResult && !!mobileScore && !!mobileScore.vmResult) {
          const metricMap = new Map<string, VitalMetricInfo>();
          this.fillVitalMetricResults(DEVICES.DESKTOP, metricMap, desktopScore.vmResult);
          this.fillVitalMetricResults(DEVICES.MOBILE, metricMap, mobileScore.vmResult);

          const tableData = {
            records: this.convertMetricMapToTable(metricMap),
            updateDate: new Date(),
          } as VitalMetricsTable;
          this.vitalMetrics$$.next(tableData);
        }
        this.refreshing$$.next(false);
      },
      () => {
        const errorMessage = this.translateService.instant('API_ERROR.PAGESPEED_MAX_QUOTA');
        this.alertService.openErrorSnack(errorMessage);
        this.refreshing$$.next(false);
      },
    );
  }
}

export function GetVitalMetricsRequest(partnerId: string, accountId: string): QueryMetricsRequest {
  return new QueryMetricsRequest({
    partnerId: partnerId,
    metricName: 'google_pagespeed_vital_metrics',
    resourceIds: [
      {
        businessId: { businessId: accountId },
      },
    ],
    measures: [
      {
        aggregate: new Aggregate({
          measure: 'numeric_value',
          aggOp: Operator.AVG,
        }),
      },
    ],
    groupBy: new GroupBy({
      dimension: [
        new Dimension({
          dimension: 'collection_datetime',
        }),
        new Dimension({
          dimension: 'metric_id',
        }),
        new Dimension({
          dimension: 'device',
        }),
      ],
    }),
  });
}
